import { useMemo, useState, useEffect, useCallback } from 'react';
import { Sparkles, User, Users, School, Plus } from 'lucide-react';
import { hasGenerator, getCategories, generateExercice } from '../../generators';
import { composerAuto, ciblePossible, TAILLE_DEFAUT, CIBLE_CARTES, CIBLE_VARIE } from '../../utils/compositionAuto';
import ComposerAutoBouton from './ComposerAutoBouton';
import { exerciseurOuvert, MENTION_EXERCISEURS_A_VENIR } from '../../generators/ouverture';
import { useUser } from '../../context/UserContext';
import ExerciseurCard from './ExerciseurCard';
import BanqueQuestionCard from './BanqueQuestionCard';
import SectionPliable from './SectionPliable';

const SANS_PROF = 'sans_prof';
/** Durée d'affichage du simple succès de composition (« 8 exercices ajoutés »). */
const DUREE_RETOUR_MS = 5000;

/**
 * Affiche les exercices d'un seul attendu :
 *   1. « Exerciseurs TanoEd » (pliable, ouvert par défaut) ;
 *   2. « Exercices du collège {nom} » : les questions de banque classées par
 *      CRÉATEUR, un groupe pliable par prof (« Mes questions » pour le prof
 *      connecté, « Les questions de Prénom Nom » pour un collègue) ; un seul
 *      créateur → déplié ; plusieurs → seul le groupe du prof connecté est
 *      déplié.
 * Contenu équivalent à ce qui était imbriqué dans un attendu de BanqueQuestionQCM,
 * mais en standalone pour le nouveau flow panel.
 */
const AttenduExercices = ({
    attenduId, rubrique, description, themeName, descName,
    questions, niveau, matiere, niveauChar, niveauAttendu,
    selectable, selectedExercices, onUpdateExercice,
    selectedIds, onToggleSelect, onPreview, onCreateQuestion, onEditQuestion, onDeleteQuestion,
    modeRevue = false, destination = null,
}) => {
    const { user, etablissementData } = useUser() || {};
    // Exerciseurs TanoEd : exposés selon la politique d'ouverture (niveau,
    // rubrique de l'attendu, liste explicite — generators/ouverture.js) ;
    // sinon une mention « bientôt disponible » remplace les cartes, si
    // l'attendu a bien un générateur. Le niveau est celui de l'ATTENDU
    // (décision prof 2026-09-29 : une 4e qui prend un objectif de 6e suit la
    // règle de la 6e) ; à défaut, celui de la classe.
    const ouverts = exerciseurOuvert(niveauAttendu || niveau, { rubrique, attenduId });
    const aGenerateur = hasGenerator(attenduId);
    const cats = useMemo(
        () => (ouverts && aGenerateur ? getCategories(attenduId) : []),
        [attenduId, ouverts, aGenerateur]
    );
    const exerciseursAVenir = !ouverts && aGenerateur;

    // Provenance : une question avec generator_key est une instance générée
    // par un exerciseur (rattachée à sa carte via le ▾ « réutiliser ») —
    // les groupes du collège ne montrent que les créations manuelles.
    const questionsAttendu = useMemo(
        () => (questions || []).filter(q => q.attendu === attenduId),
        [questions, attenduId]
    );
    const profQuestions = useMemo(
        () => questionsAttendu.filter(q => !q.generator_key),
        [questionsAttendu]
    );
    // Map<formatKey, question[]> des instances générées de l'attendu
    const generesParFormat = useMemo(() => {
        const m = new Map();
        for (const q of questionsAttendu) {
            if (!q.generator_key) continue;
            if (!m.has(q.generator_key)) m.set(q.generator_key, []);
            m.get(q.generator_key).push(q);
        }
        // Plus récentes d'abord
        for (const liste of m.values()) {
            liste.sort((a, b) => (b.date_creation || '').localeCompare(a.date_creation || ''));
        }
        return m;
    }, [questionsAttendu]);

    // ─── Groupes par créateur ───
    const monProfId = user?.role === 'professeur' ? user?.id : null;
    const groupes = useMemo(() => {
        const parProf = new Map();
        for (const q of profQuestions) {
            const cle = q.professeur ?? SANS_PROF;
            if (!parProf.has(cle)) parProf.set(cle, []);
            parProf.get(cle).push(q);
        }
        const profs = etablissementData?.professeurs || [];
        const nomDe = (id) => {
            const p = profs.find((x) => x.id === id);
            return p ? `${p.prenom || ''} ${p.nom || ''}`.trim() : '';
        };
        const liste = [...parProf.entries()].map(([cle, qs]) => {
            const estMoi = cle !== SANS_PROF && cle === monProfId;
            const nom = cle === SANS_PROF ? '' : nomDe(cle);
            let titre;
            if (estMoi) titre = 'Mes questions';
            else if (cle === SANS_PROF) titre = 'Autres questions';
            else titre = nom ? `Les questions de ${nom}` : 'Les questions d\'un autre professeur';
            qs.sort((a, b) => (b.date_creation || '').localeCompare(a.date_creation || ''));
            return { cle, titre, estMoi, nom, questions: qs };
        });
        // Le prof connecté d'abord, puis les collègues par nom, « Autres » en dernier
        liste.sort((a, b) => {
            if (a.estMoi !== b.estMoi) return a.estMoi ? -1 : 1;
            if ((a.cle === SANS_PROF) !== (b.cle === SANS_PROF)) return a.cle === SANS_PROF ? 1 : -1;
            return a.nom.localeCompare(b.nom);
        });
        return liste;
    }, [profQuestions, etablissementData, monProfId]);

    // ─── Plis : exerciseurs ouverts par défaut ; groupes : un seul → ouvert,
    // plusieurs → seul le mien. Tant que le prof n'a rien plié/déplié
    // (`groupesOuverts === null`), l'ouverture est DÉRIVÉE des groupes ; ses
    // clics figent ensuite un Set. Réinitialisés quand l'attendu change. ───
    const [sectionTanoedOuverte, setSectionTanoedOuverte] = useState(true);
    const [groupesOuverts, setGroupesOuverts] = useState(null);
    useEffect(() => {
        setSectionTanoedOuverte(true);
        setGroupesOuverts(null);
    }, [attenduId]);
    const ouvertParDefaut = (g) => groupes.length === 1 || g.estMoi;
    const estOuvert = (g) => (groupesOuverts ? groupesOuverts.has(g.cle) : ouvertParDefaut(g));
    const toggleGroupe = (g) => setGroupesOuverts((prev) => {
        const next = new Set(prev || groupes.filter(ouvertParDefaut).map((x) => x.cle));
        if (next.has(g.cle)) next.delete(g.cle); else next.add(g.cle);
        return next;
    });

    const nomCollege = etablissementData?.nom;

    // ─── « Composer N questions » (utils/compositionAuto.js) : la logique est
    // dans le module pur, ici on applique les ajouts par le chemin existant
    // (onUpdateExercice, incréments unitaires, compte tenu par clé) et on
    // affiche le retour. Le retour est rangé avec l'attendu : il disparaît
    // de lui-même quand on change d'attendu (dérivé, pas d'effet). Le simple
    // succès (« 8 exercices ajoutés ») s'efface aussi après quelques secondes,
    // sinon il se lit comme un total et contredit la barre du bas, qui compte
    // tout le panier (allègement 2026-09-03) ; les messages qui apprennent
    // quelque chose (rien de plus à proposer, remplacement…) restent. ───
    const [tailleCompo, setTailleCompo] = useState(TAILLE_DEFAUT);
    const [retourCompo, setRetourCompo] = useState(null); // { attenduId, texte, ephemere }
    useEffect(() => {
        if (!retourCompo?.ephemere) return undefined;
        const t = setTimeout(() => setRetourCompo((r) => (r === retourCompo ? null : r)), DUREE_RETOUR_MS);
        return () => clearTimeout(t);
    }, [retourCompo]);
    const composer = useCallback((cible) => {
        const { ajouts, retraits, nbRetires, manque, nbManuels } = composerAuto({
            cartes: cats, attenduId, cible, n: tailleCompo, dejaPris: selectedExercices, generateExercice,
            contexte: { descName, attenduDescription: description },
        });
        // La composition précédente s'efface (les manuels restent : on remplace
        // les instances de chaque clé touchée par ses seules manuelles)…
        const compteurs = new Map();
        retraits.forEach(({ cle, payloads }) => {
            onUpdateExercice?.(cle, payloads.length, payloads);
            compteurs.set(cle, payloads.length);
        });
        // …puis ajouts, par incréments unitaires, compte tenu par clé.
        ajouts.forEach(({ cle, payload }) => {
            const base = compteurs.get(cle) ?? (selectedExercices?.get(cle)?.payloads?.length || 0);
            onUpdateExercice?.(cle, base + 1, payload);
            compteurs.set(cle, base + 1);
        });
        const nb = ajouts.length;
        const quoi = cible === CIBLE_CARTES ? 'QCM' : `exercice${nb > 1 ? 's' : ''}`;
        let texte;
        const ephemere = nb > 0 && nbRetires === 0 && manque === 0;
        if (nb === 0 && nbManuels >= tailleCompo) {
            texte = `${nbManuels} exercice${nbManuels > 1 ? 's' : ''} déjà choisi${nbManuels > 1 ? 's' : ''} à la main pour cet attendu : rien à composer.`;
        } else if (nb === 0) {
            texte = 'Cet attendu ne propose pas ce format.';
        } else {
            texte = `${nb} ${quoi} ${nbRetires > 0 ? 'remplacent la composition précédente' : `ajouté${nb > 1 ? 's' : ''}`}`
                + `${nbManuels > 0 ? `, avec ${nbManuels} choisi${nbManuels > 1 ? 's' : ''} à la main` : ''}`
                + `${manque > 0 ? ' ; cet attendu n\'en propose pas davantage' : ''}.`;
        }
        setRetourCompo({ attenduId, texte, ephemere });
    }, [cats, attenduId, tailleCompo, selectedExercices, descName, description, onUpdateExercice]);
    const messageCompo = retourCompo && retourCompo.attenduId === attenduId ? retourCompo.texte : null;

    return (
        <div className="space-y-6">
            {/* Plus de fil d'Ariane thème › descripteur › attendu : la barre
                collante de l'écran de composition l'affiche déjà, en
                permanence (chantier composition, 2026-09-02). */}

            {/* Section Exerciseurs TanoEd (pliable) */}
            {cats.length > 0 && (
                <SectionPliable
                    titre="Exerciseurs TanoEd"
                    icone={<Sparkles size={13} />}
                    compteur={cats.length}
                    couleur="blue"
                    ouvert={sectionTanoedOuverte}
                    onToggle={() => setSectionTanoedOuverte((v) => !v)}
                    action={selectable ? (
                        <ComposerAutoBouton
                            taille={tailleCompo}
                            onTaille={setTailleCompo}
                            cartesPossible={ciblePossible(cats, CIBLE_CARTES)}
                            variePossible={ciblePossible(cats, CIBLE_VARIE)}
                            onComposer={composer}
                        />
                    ) : null}
                >
                    {messageCompo && (
                        <p className="mb-3 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
                            <Sparkles size={13} />
                            {messageCompo}
                        </p>
                    )}
                    <div className="columns-1 lg:columns-2 gap-4 space-y-4">
                        {cats.map(cat => (
                            // Clé PAR ATTENDU (bug 2026-09-08) : deux attendus consécutifs
                            // qui partagent un id de carte (« flash » de 1049 puis 1050)
                            // réutilisaient le même composant, dont l'exercice généré au
                            // montage restait celui de l'ancien attendu.
                            <ExerciseurCard
                                key={`${attenduId}_${cat.id}`}
                                attenduId={attenduId}
                                category={cat}
                                niveau={niveau}
                                matiere={matiere}
                                niveauChar={niveauChar}
                                selectable={selectable}
                                exoKeyPrefix={`${attenduId}_${cat.id}`}
                                selectedExercices={selectedExercices}
                                onUpdateExercice={onUpdateExercice}
                                onPreview={onPreview}
                                descName={descName}
                                attenduDescription={description}
                                generesParFormat={generesParFormat}
                                selectedIds={selectedIds}
                                onToggleSelect={onToggleSelect}
                                onDeleteQuestion={onDeleteQuestion}
                                modeRevue={modeRevue}
                                destination={destination}
                            />
                        ))}
                    </div>
                </SectionPliable>
            )}

            {/* Exerciseur non ouvert (niveau, rubrique ou liste,
                generators/ouverture.js) : il existe mais n'est pas encore exposé */}
            {exerciseursAVenir && (
                <span
                    className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 text-gray-400 rounded-full px-2.5 py-1 text-xs font-semibold"
                    title={`L'exerciseur TanoEd de cet attendu n'est pas encore ouvert : ${MENTION_EXERCISEURS_A_VENIR.toLowerCase()}.`}
                >
                    <Sparkles size={13} />
                    Exerciseurs TanoEd · {MENTION_EXERCISEURS_A_VENIR}
                </span>
            )}

            {/* Section Exercices du collège : questions manuelles (generator_key
                null) classées par créateur, un groupe pliable par prof.
                Modifier / Supprimer réservés au propriétaire (vérifié dans la
                carte, et par le back). */}
            {profQuestions.length > 0 && (
                <div>
                    <span className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 text-slate-600 rounded-full px-2.5 py-1 text-xs font-semibold mb-3">
                        <School size={13} />
                        {nomCollege ? `Exercices du collège ${nomCollege}` : 'Exercices du collège'} · {profQuestions.length}
                    </span>
                    <div className="space-y-4 pl-1">
                        {groupes.map((g) => (
                            <SectionPliable
                                key={g.cle}
                                titre={g.titre}
                                icone={g.estMoi ? <User size={13} /> : <Users size={13} />}
                                compteur={g.questions.length}
                                couleur="slate"
                                ouvert={estOuvert(g)}
                                onToggle={() => toggleGroupe(g)}
                            >
                                <div className="columns-1 lg:columns-2 gap-4 space-y-4">
                                    {g.questions.map(q => (
                                        <BanqueQuestionCard
                                            key={q.id}
                                            question={q}
                                            selectable={selectable}
                                            selected={selectable ? Boolean(selectedIds?.has(q.id)) : false}
                                            onToggleSelect={onToggleSelect}
                                            onPreview={onPreview}
                                            onEditQuestion={onEditQuestion}
                                            onDeleteQuestion={onDeleteQuestion}
                                        />
                                    ))}
                                </div>
                            </SectionPliable>
                        ))}
                    </div>
                </div>
            )}

            {/* État vide — ni TanoEd (ouvert ou à venir) ni prof */}
            {cats.length === 0 && !exerciseursAVenir && profQuestions.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <p className="text-sm text-gray-400">Aucun exercice disponible pour cet attendu.</p>
                </div>
            )}

            {/* Bouton créer une question personnalisée. Sur le fond bleu clair
                de la composition, le pointillé bleu pâle ne se voyait pas :
                fond blanc, encre bleue franche, graisse (retour prof 2026-09-02). */}
            {onCreateQuestion && (
                <button
                    onClick={onCreateQuestion}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-blue-400 bg-white text-blue-700 hover:border-blue-600 hover:bg-blue-50 text-[15px] font-bold shadow-sm transition-all"
                >
                    <Plus size={16} />
                    Créer une question personnalisée
                </button>
            )}

            {/* Espace pour la sticky bar en bas */}
            <div className="h-24" />
        </div>
    );
};

export default AttenduExercices;
